import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box, Dialog, IconButton, Typography, Stack, Alert, Slider, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScaleIcon from "@mui/icons-material/Scale";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { QuizButton } from "../../shared/QuizButton";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import { safeJson, getProp, findHiddenPriceProduct, getPackPriceFromHidden, getBundleVariantId } from "../helpers";
import { getPackPricingConfig, toDailyKey50, type PlanDays } from "@/helpers/plansToPacks";
import { buildMealSelectionsWithBaseline, type MealSelectionWithBaseline } from "@/helpers/mealDistribution";
import { buildVariantIndex } from "../../../utils/recharge";
import type { SubscriptionData } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  products: any[];
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

type Step = "serving" | "meals" | "confirm";
type MealSelection = MealSelectionWithBaseline;

const MIN_DAILY_GRAMS = 50;
const MAX_DAILY_GRAMS = 2000;

function findMealProducts(products: any[]): any[] {
  return products.filter((p: any) => {
    const type = String(p.product_type || "").toLowerCase();
    const tags = String(p.tags || "").toLowerCase();
    const handle = String(p.handle || "").toLowerCase();
    if (handle.includes("hidden")) return false;
    return type === "recipe" || tags.includes("recipe") || tags.includes("meal");
  });
}

function findVariantForPackSize(product: any, packSize: number): any | null {
  if (!product.variants?.length) return null;
  const matchingVariant = product.variants.find((v: any) => {
    const titleMatch = String(v.title || "").match(/(\d+)/);
    return titleMatch && parseInt(titleMatch[1], 10) === packSize;
  });
  return matchingVariant || product.variants[0];
}

function buildMealsSummary(meals: MealSelection[], packSize: number): string {
  return meals.filter(m => m.count > 0).map(m => `${m.count}× ${m.productTitle} ${packSize}g`).join(", ");
}

export function ChangeDailyServingModal({ open, onClose, sub, products, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [step, setStep] = useState<Step>("serving");
  const [selectedDailyGrams, setSelectedDailyGrams] = useState(300);
  const [mealSelections, setMealSelections] = useState<MealSelection[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const variantIndex = useMemo(() => buildVariantIndex(products), [products]);

  const currentValues = useMemo(() => {
    if (!sub?.properties) return { dailyGrams: 300, packSizeGrams: 400, planDays: 7 as PlanDays };
    const packSizeProp = sub.properties.find(p => p.name === "_paws_pack_size_grams");
    const packSizeGrams = packSizeProp ? parseInt(packSizeProp.value, 10) : 400;
    const planDaysProp = sub.properties.find(p => p.name === "_paws_plan_days");
    let planDays = planDaysProp ? parseInt(planDaysProp.value, 10) : 7;
    if (![7, 14, 28].includes(planDays)) planDays = 7;
    const dogJsonProp = sub.properties.find(p => p.name === "_paws_dog_json");
    let dailyGrams = 300;
    if (dogJsonProp?.value) {
      try { dailyGrams = JSON.parse(dogJsonProp.value).dailyGrams || 300; } catch {}
    }
    const clampedDailyGrams = Math.min(MAX_DAILY_GRAMS, Math.max(MIN_DAILY_GRAMS, dailyGrams));
    return { dailyGrams: clampedDailyGrams, packSizeGrams, planDays: planDays as PlanDays };
  }, [sub]);

  const newPackConfig = useMemo(() => {
    const { planDays } = currentValues;
    const { dailyKey, packSize, totalPacks } = getPackPricingConfig(selectedDailyGrams, planDays);
    const hiddenProduct = findHiddenPriceProduct(products, planDays);
    const pricePerPack = getPackPriceFromHidden(hiddenProduct, packSize);
    const totalPrice = totalPacks * pricePerPack;
    const pricePerDay = totalPrice / planDays;
    const bundleVariantId = getBundleVariantId(products, planDays, packSize);
    return { dailyKey, packSize, totalPacks, pricePerPack, totalPrice, pricePerDay, bundleVariantId };
  }, [selectedDailyGrams, currentValues, products]);

  const mealProducts = useMemo(() => findMealProducts(products), [products]);
  const totalMealsSelected = useMemo(() => mealSelections.reduce((s, m) => s + m.count, 0), [mealSelections]);
  const packSizeChanged = newPackConfig.packSize !== currentValues.packSizeGrams;
  const hasChanged = selectedDailyGrams !== currentValues.dailyGrams;

  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open && !wasOpen) {
      setStep("serving");
      setSelectedDailyGrams(currentValues.dailyGrams);
      setError(null);
      setSuccess(false);
      setConfirming(false);
      setMealSelections([]);
    }
  }, [open, currentValues]);

  const handleNext = () => {
    if (step === "serving") {
      const distributed = buildMealSelectionsWithBaseline(mealProducts, newPackConfig.totalPacks, newPackConfig.packSize, findVariantForPackSize);
      setMealSelections(distributed);
      setError(null);
      setStep("meals");
    } else if (step === "meals") {
      if (totalMealsSelected < newPackConfig.totalPacks) { setError(`Please select at least ${newPackConfig.totalPacks} meals`); return; }
      setError(null);
      setStep("confirm");
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === "meals") setStep("serving");
    else if (step === "confirm") setStep("meals");
  };

  const handleMealChange = useCallback((variantId: string, delta: number, product: any, variant: any) => {
    setMealSelections(prev => {
      const existing = prev.find(m => m.variantId === variantId);
      if (existing) {
        const newCount = Math.max(0, existing.count + delta);
        return prev.map(m => m.variantId === variantId ? { ...m, count: newCount } : m);
      }
      if (delta > 0) {
        return [...prev, {
          variantId, count: delta, baseline: 0,
          productTitle: product.title, variantTitle: variant.title || "",
          imageUrl: product.images?.[0]?.src || (product.image as any)?.src || null,
          price: parseFloat(String(variant.price || "0")),
        }];
      }
      return prev;
    });
  }, []);

  const handleConfirm = async () => {
    if (!newPackConfig.bundleVariantId) { setError("Unable to find bundle variant."); return; }
    if (!newPackConfig.totalPrice || newPackConfig.totalPrice <= 0) { setError("Unable to calculate pricing."); return; }
    setConfirming(true);
    setError(null);
    try {
      const activeMeals = mealSelections.filter(m => m.count > 0);
      const mealsSummary = buildMealsSummary(activeMeals, newPackConfig.packSize);
      const meals = activeMeals.map(m => ({
        variantId: String(m.variantId),
        count: m.count,
        productTitle: m.productTitle || "",
        variantTitle: m.variantTitle || "",
        price: newPackConfig.pricePerPack,
      }));
      const res = await call("/recharge/subscription/change-daily-serving", {
        method: "POST",
        body: {
          subscriptionId: sub!.id,
          newDailyGrams: selectedDailyGrams,
          newPackSizeGrams: newPackConfig.packSize,
          newVariantId: newPackConfig.bundleVariantId,
          meals,
          newPrice: newPackConfig.totalPrice,
          mealsSummary,
        },
      });
      if (!res.ok) throw new Error(res.error || "Failed to change daily serving");
      setSuccess(true);
      setTimeout(() => { onSuccess(`Daily serving updated to ${selectedDailyGrams}g`); onClose(); onRefresh(); }, 1200);
    } catch (err: any) {
      setError(err.message || "Failed to change daily serving");
    } finally {
      setConfirming(false);
    }
  };

  const formatPrice = (price: number) => `£${price.toFixed(2)}`;
  const steps = [
    { key: "serving", label: "Daily Serving" },
    { key: "meals", label: "Select Meals" },
    { key: "confirm", label: "Confirm" },
  ];
  const currentStepIndex = steps.findIndex(s => s.key === step);

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth={step === "confirm" ? "md" : "sm"}
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh", display: "flex", flexDirection: "column" } }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 3, borderBottom: "1px solid rgba(0,0,0,.08)", background: QUIZ.color.cream, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {step !== "serving" && (
            <IconButton onClick={handleBack} disabled={confirming} size="small" sx={{ mr: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", background: "linear-gradient(135deg, #FFE8D6 0%, #FFCBA4 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <RestaurantIcon sx={{ fontSize: 22, color: COLORS.brown }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.brownDeep }}>
              {steps[currentStepIndex]?.label || "Change Daily Serving"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.brownDeep, opacity: 0.65 }}>
              Step {currentStepIndex + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={confirming} sx={{ color: COLORS.brownDeep }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        {success && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: "12px", mb: 2 }}>
            Daily serving updated successfully!
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ borderRadius: "12px", mb: 2 }}>{error}</Alert>}

        {/* Step 1: Slider */}
        {step === "serving" && !success && (
          <Box>
            <Typography sx={{ fontSize: 14, color: COLORS.brownDeep, opacity: 0.75, mb: 3 }}>
              Adjust the daily serving size. This will update the pack size and delivery quantity.
            </Typography>

            <Box sx={{ textAlign: "center", mb: 3, p: 2, borderRadius: "12px", background: "#FFF8F0", border: "2px solid #FFD9B3" }}>
              <Typography sx={{ fontSize: 48, fontWeight: 800, color: COLORS.brown, lineHeight: 1 }}>
                {selectedDailyGrams}g
              </Typography>
              <Typography sx={{ fontSize: 14, color: COLORS.brownDeep, opacity: 0.65, mt: 1 }}>per day</Typography>
            </Box>

            <Box sx={{ px: 2, mb: 3 }}>
              <Slider
                value={selectedDailyGrams}
                onChange={(_, value) => setSelectedDailyGrams(value as number)}
                min={MIN_DAILY_GRAMS} max={MAX_DAILY_GRAMS} step={50}
                marks={[{ value: 50, label: "50g" }, { value: 500, label: "500g" }, { value: 1000, label: "1000g" }, { value: 1500, label: "1500g" }, { value: 2000, label: "2000g" }]}
                sx={{ color: COLORS.brown, "& .MuiSlider-thumb": { width: 24, height: 24, backgroundColor: QUIZ.color.warmWhite, border: `3px solid ${COLORS.brown}` }, "& .MuiSlider-markLabel": { fontSize: 11 } }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ p: 2, borderRadius: "12px", background: QUIZ.color.cream, border: "1px solid rgba(0,0,0,.06)" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.brownDeep, mb: 1.5 }}>
                Your {currentValues.planDays}-Day Plan will include:
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ScaleIcon sx={{ fontSize: 18, color: COLORS.brown }} />
                    <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>Pack size</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.brownDeep }}>
                    {newPackConfig.packSize}g
                    {packSizeChanged && <Typography component="span" sx={{ fontSize: 12, color: "#666", ml: 1 }}>(was {currentValues.packSizeGrams}g)</Typography>}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: COLORS.brown }} />
                    <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>Packs per delivery</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.brownDeep }}>{newPackConfig.totalPacks} packs</Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>Total per delivery</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.brown }}>{formatPrice(newPackConfig.totalPrice)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 13, color: COLORS.brownDeep, opacity: 0.65 }}>Per day</Typography>
                  <Typography sx={{ fontSize: 13, color: COLORS.brownDeep, opacity: 0.65 }}>{formatPrice(newPackConfig.pricePerDay)}/day</Typography>
                </Box>
              </Stack>
            </Box>

            {packSizeChanged && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: "12px" }}>
                Pack size will change from {currentValues.packSizeGrams}g to {newPackConfig.packSize}g. You'll need to re-select meals.
              </Alert>
            )}
          </Box>
        )}

        {/* Step 2: Meals */}
        {step === "meals" && !success && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ fontSize: 14, color: COLORS.brownDeep, opacity: 0.75 }}>
                Select your meals ({newPackConfig.totalPacks} required)
              </Typography>
              <Box sx={{ px: 1.5, py: 0.5, borderRadius: "8px", background: totalMealsSelected >= newPackConfig.totalPacks ? "#E5FFEA" : "#FFF3CD", color: totalMealsSelected >= newPackConfig.totalPacks ? "#2E7D32" : "#856404", fontWeight: 700, fontSize: 13 }}>
                {totalMealsSelected} / {newPackConfig.totalPacks}
              </Box>
            </Box>

            <Stack spacing={1.5}>
              {mealProducts.map((product: any) => {
                const variant = findVariantForPackSize(product, newPackConfig.packSize);
                if (!variant) return null;
                const variantId = String(variant.id);
                const selection = mealSelections.find(m => m.variantId === variantId);
                const count = selection?.count || 0;
                const imageUrl = variantIndex.get(variantId)?.imageUrl;

                return (
                  <Box key={product.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: "12px", border: count > 0 ? `2px solid ${COLORS.brown}` : "1px solid rgba(0,0,0,.08)", background: count > 0 ? "#FFF8F0" : QUIZ.color.cream }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: "8px", overflow: "hidden", flexShrink: 0, background: QUIZ.color.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {imageUrl ? (
                        <Box component="img" src={imageUrl} alt={product.title} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <RestaurantIcon sx={{ fontSize: 24, color: "#C4A98A" }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.brownDeep, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{product.title}</Typography>
                      <Typography sx={{ fontSize: 12, color: COLORS.brownDeep, opacity: 0.65 }}>{newPackConfig.packSize}g pack</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                      <IconButton size="small" onClick={() => handleMealChange(variantId, -1, product, variant)} disabled={count <= 0} sx={{ width: 32, height: 32, border: "1px solid rgba(0,0,0,.12)", "&:disabled": { opacity: 0.3 } }}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography sx={{ width: 32, textAlign: "center", fontWeight: 700, fontSize: 16 }}>{count}</Typography>
                      <IconButton size="small" onClick={() => handleMealChange(variantId, 1, product, variant)} sx={{ width: 32, height: 32, background: COLORS.brown, color: QUIZ.color.white, "&:hover": { background: COLORS.brownDark } }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Step 3: Confirm */}
        {step === "confirm" && !success && (
          <Box>
            <Typography sx={{ fontSize: 14, color: COLORS.brownDeep, opacity: 0.75, mb: 3 }}>
              Review your changes before confirming.
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ p: 2, borderRadius: "12px", background: QUIZ.color.cream, border: "1px solid rgba(0,0,0,.06)" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.brownDeep, opacity: 0.65, mb: 1 }}>DAILY SERVING</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>{currentValues.dailyGrams}g &rarr; {selectedDailyGrams}g</Typography>
                  {hasChanged && (
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: selectedDailyGrams > currentValues.dailyGrams ? COLORS.green : COLORS.red }}>
                      {selectedDailyGrams > currentValues.dailyGrams ? "+" : ""}{selectedDailyGrams - currentValues.dailyGrams}g
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ p: 2, borderRadius: "12px", background: QUIZ.color.cream, border: "1px solid rgba(0,0,0,.06)" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.brownDeep, opacity: 0.65, mb: 1 }}>PACK SIZE</Typography>
                <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>
                  {currentValues.packSizeGrams}g &rarr; {newPackConfig.packSize}g ({newPackConfig.totalPacks} packs)
                </Typography>
              </Box>
              <Box sx={{ p: 2, borderRadius: "12px", background: QUIZ.color.cream, border: "1px solid rgba(0,0,0,.06)" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.brownDeep, opacity: 0.65, mb: 1 }}>MEALS ({totalMealsSelected} packs)</Typography>
                <Stack spacing={0.5}>
                  {mealSelections.filter(m => m.count > 0).map(m => (
                    <Typography key={m.variantId} sx={{ fontSize: 13, color: COLORS.brownDeep }}>{m.count}× {m.productTitle}</Typography>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ p: 2, borderRadius: "12px", background: "#FFF8F0", border: "2px solid #FFD9B3" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.brown, mb: 1 }}>NEW PRICE</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 14, color: COLORS.brownDeep }}>Per delivery</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: COLORS.brown }}>{formatPrice(newPackConfig.totalPrice)}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: COLORS.brownDeep, opacity: 0.65, textAlign: "right" }}>({formatPrice(newPackConfig.pricePerDay)}/day)</Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Footer */}
      {!success && (
        <Box sx={{ p: 3, borderTop: "1px solid rgba(0,0,0,.08)", background: QUIZ.color.cream, flexShrink: 0 }}>
          <Stack direction="row" spacing={1.5}>
            <QuizButton variant="secondary" onClick={step === "serving" ? onClose : handleBack} disabled={confirming} fullWidth>
              {step === "serving" ? "Cancel" : "Back"}
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={step === "confirm" ? handleConfirm : handleNext}
              disabled={confirming || (step === "serving" && !hasChanged && !packSizeChanged) || (step === "meals" && totalMealsSelected < newPackConfig.totalPacks)}
              fullWidth
            >
              {confirming ? "Updating..." : step === "confirm" ? "Confirm Changes" : "Next"}
            </QuizButton>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
